import type { Command } from '../sim/commands';
import type { PlayerId } from '../sim/ids';
import type { ModeId } from '../sim/modes/modes';

/**
 * Wire protocol between the game client and the room server (Phase 10,
 * MULTIPLAYER_ARCHITECTURE.md, DECISIONS D87–D92). JSON over one WebSocket.
 *
 * Model: server-ordered deterministic lockstep with a server-authoritative
 * simulation. The server runs the same engine-free Simulation, validates every
 * command against ITS state, and broadcasts, per tick, the commands it
 * accepted. Clients run the same Simulation and only step a tick once they have
 * the server's message for it, so everyone applies the same commands at the
 * same tick. State hashes are compared regularly to detect desync.
 */
export const PROTOCOL_VERSION = 1;
export const SERVER_TICK_RATE = 10;
/** The server sends its state hash every this many ticks. */
export const HASH_EVERY_TICKS = 50;
/** A player who stays disconnected this long during a match forfeits. */
export const RECONNECT_GRACE_SECONDS = 90;

export interface RoomOptions {
  readonly mode: ModeId;
  readonly teams: 'ffa' | '2v2';
  /** Test / private-room options (only honoured when the room was created with dev = true). */
  readonly dev?: { readonly units?: number; readonly speed?: number; readonly zombies?: number; readonly waves?: boolean };
  /** Raid rooms (Phase 13): the defender profile whose saved base is raided (loaded by the server). */
  readonly defender?: string;
}

export interface LobbySeat {
  readonly seat: number;
  readonly playerId: PlayerId;
  readonly name: string;
  readonly ready: boolean;
  readonly connected: boolean;
  readonly host: boolean;
  readonly team: number;
}

export interface MatchStart {
  readonly seed: number;
  readonly map: { readonly width: number; readonly height: number };
  readonly options: RoomOptions;
  readonly players: readonly { readonly id: PlayerId; readonly name: string; readonly slot: number; readonly team: number }[];
  /** Raid: the defender's base layout (validated by the server). */
  readonly base?: import('../sim/raid/bases').BaseLayout;
}

export type ClientMessage =
  | { t: 'hello'; v: number; name: string; session?: string; profile?: { id: string; token: string } }
  | { t: 'options'; options: RoomOptions }
  | { t: 'ready'; ready: boolean }
  | { t: 'start' }
  | { t: 'cmd'; seq: number; cmd: Command }
  | { t: 'resync' }
  | { t: 'leave' }
  | { t: 'ping'; at: number };

export type ServerMessage =
  | { t: 'welcome'; session: string; playerId: PlayerId; room: string }
  | { t: 'lobby'; seats: readonly LobbySeat[]; options: RoomOptions; started: boolean }
  /** The match begins (or, after a reconnect, began): build the same Simulation, then replay `log` up to `tick`. */
  | { t: 'start'; start: MatchStart; tick: number; log: readonly TickEntry[] }
  | { t: 'tick'; tick: number; cmds: readonly { readonly playerId: PlayerId; readonly cmd: Command }[] }
  | { t: 'reject'; seq: number; code: string; message: string }
  | { t: 'hash'; tick: number; hash: string }
  | { t: 'error'; code: string; message: string }
  | { t: 'pong'; at: number };

/** One tick in the command log (ticks with no commands are not logged). */
export interface TickEntry {
  readonly tick: number;
  readonly cmds: readonly { readonly playerId: PlayerId; readonly cmd: Command }[];
}
